import { useState } from "react";
import { waLink, trackEvent, REQUIREMENT_TYPES } from "../lib/api";

const VARIANTS = {
  contact: {
    type: "contact",
    event: "contact_form",
    fields: ["name", "organisation", "designation", "phone", "email", "city", "requirement_type", "message"],
  },
  quote: {
    type: "quote",
    event: "quote_request",
    fields: ["name", "organisation", "phone", "email", "product_name", "quantity", "location", "message"],
  },
  service: {
    type: "service",
    event: "service_request",
    fields: ["name", "organisation", "phone", "email", "message"],
  },
};

const LABELS = {
  name: "Full Name *", organisation: "Organisation", designation: "Designation", phone: "Phone Number *",
  email: "Email", city: "City", product_name: "Product", quantity: "Quantity", location: "Location",
  message: "Requirement / Message", requirement_type: "Requirement Type",
};

const FIELD_LABELS = {
  name: "Name", organisation: "Organisation", designation: "Designation", phone: "Phone",
  email: "Email", city: "City", product_name: "Product", quantity: "Quantity", location: "Location",
  message: "Requirement", requirement_type: "Requirement Type",
};

const LeadForm = ({ variant = "contact", defaults = {}, onDone, dark = false, compact = false }) => {
  const cfg = VARIANTS[variant] || VARIANTS.contact;
  const [form, setForm] = useState({ ...defaults });
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      setError("Name and phone number are required.");
      return;
    }
    setError("");
    trackEvent(cfg.event, { variant });
    const lines = [
      `Hello AccuSurge — new ${cfg.type} enquiry from the website.`,
      "",
      ...Object.entries(FIELD_LABELS)
        .filter(([k]) => form[k])
        .map(([k, label]) => `${label}: ${form[k]}`),
    ];
    window.open(waLink(lines.join("\n")), "_blank", "noopener,noreferrer");
    setState("done");
    onDone && onDone();
  };

  if (state === "done") {
    return (
      <div
        className={`rounded-lg border p-8 text-center ${dark ? "border-white/15 bg-white/5" : "border-brand-200 bg-brand-50"}`}
        data-testid="lead-form-success"
      >
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent/15">
          <svg viewBox="0 0 24 24" className="h-6 w-6 text-accent-600" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M4 12l5 5L20 6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <h3 className={`mt-4 font-heading text-xl font-semibold ${dark ? "text-white" : "text-ink"}`}>
          Almost done — your enquiry is ready in WhatsApp
        </h3>
        <p className={`mt-2 text-sm ${dark ? "text-white/70" : "text-slate-600"}`}>
          We've opened WhatsApp with your details filled in. Press send there and our team will respond.
          You can also call us directly at +91 90073 37749.
        </p>
        <button onClick={() => setState("idle")} className="btn-outline mt-5 !h-10 text-sm" data-testid="lead-form-again">
          Send another enquiry
        </button>
      </div>
    );
  }

  const grid = compact ? "grid grid-cols-1 gap-4 sm:grid-cols-2" : "grid grid-cols-1 gap-5 md:grid-cols-2";

  return (
    <form onSubmit={submit} className={compact ? "space-y-4" : "space-y-5"} data-testid={`lead-form-${variant}`}>
      <div className={grid}>
        {cfg.fields.map((field) =>
          field === "requirement_type" ? (
            <div key={field}>
              <label className={`field-label ${dark ? "text-white/60" : ""}`}>{LABELS[field]}</label>
              <select className="field" value={form[field] || ""} onChange={set(field)} data-testid="lead-form-requirement-type">
                <option value="">Select requirement…</option>
                {REQUIREMENT_TYPES.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
          ) : field === "message" ? (
            <div key={field} className={compact ? "sm:col-span-2" : "md:col-span-2"}>
              <label className={`field-label ${dark ? "text-white/60" : ""}`}>{LABELS[field]}</label>
              <textarea
                className="field min-h-[110px] py-2.5"
                value={form[field] || ""}
                onChange={set(field)}
                placeholder="Tell us about your facility, department or equipment requirement…"
                data-testid="lead-form-message"
              />
            </div>
          ) : (
            <div key={field}>
              <label className={`field-label ${dark ? "text-white/60" : ""}`}>{LABELS[field]}</label>
              <input
                type={field === "email" ? "email" : field === "phone" ? "tel" : "text"}
                className="field"
                value={form[field] || ""}
                onChange={set(field)}
                data-testid={`lead-form-${field}`}
              />
            </div>
          )
        )}
      </div>
      {error && (
        <p className="rounded-md border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700" data-testid="lead-form-error">
          {error}
        </p>
      )}
      <button type="submit" className="btn-accent w-full sm:w-auto" data-testid="lead-form-submit">
        Submit Enquiry via WhatsApp
      </button>
      <p className={`text-xs ${dark ? "text-white/50" : "text-slate-400"}`}>
        Submitting opens WhatsApp with your enquiry pre-filled to +91 90073 37749. Your details go directly to our team.
      </p>
    </form>
  );
};

export default LeadForm;
